import { useEffect, useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

interface PushNotificationState {
  isSupported: boolean;
  isRegistered: boolean;
  permissionStatus: "prompt" | "granted" | "denied" | "unknown";
}

type PushStatePatch = Partial<PushNotificationState>;
type PushStateListener = (patch: PushStatePatch) => void;
type PushPlugin = typeof import("@capacitor/push-notifications")["PushNotifications"];

let pushInitPromise: Promise<void> | null = null;
let pushListenersAttached = false;
let pushRegistrationStarted = false;
let activeUserId: string | null = null;
let pendingToken: string | null = null;
const pushStateListeners = new Set<PushStateListener>();

const broadcastPushState = (patch: PushStatePatch) => {
  pushStateListeners.forEach((listener) => listener(patch));
};

const persistPushToken = async (tokenValue: string, userId: string | null): Promise<boolean> => {
  pendingToken = tokenValue;
  if (!userId) {
    broadcastPushState({ isRegistered: false });
    console.warn("Push token received, but no signed-in user is available yet");
    return false;
  }

  const platform = Capacitor.getPlatform();
  const updatedAt = new Date().toISOString();
  const { data: existingToken, error: lookupError } = await supabase
    .from("push_tokens")
    .select("id")
    .eq("user_id", userId)
    .eq("token", tokenValue)
    .maybeSingle();

  if (lookupError) throw lookupError;

  if (existingToken?.id) {
    const { error } = await supabase
      .from("push_tokens")
      .update({ platform, updated_at: updatedAt })
      .eq("id", existingToken.id)
      .eq("user_id", userId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("push_tokens").insert({
      user_id: userId,
      token: tokenValue,
      platform,
      updated_at: updatedAt,
    });
    if (error) throw error;
  }

  pendingToken = null;
  broadcastPushState({ isRegistered: true });
  return true;
};

const registerForPush = async (PushNotifications: PushPlugin, force = false) => {
  if (pushRegistrationStarted && !force) return;
  pushRegistrationStarted = true;
  await PushNotifications.register();
};

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    isRegistered: false,
    permissionStatus: "unknown",
  });

  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    activeUserId = user?.id ?? null;

    if (pendingToken && activeUserId) {
      persistPushToken(pendingToken, activeUserId).catch((err) => {
        console.error("Failed to save push token:", err);
        broadcastPushState({ isRegistered: false });
      });
    }
  }, [user?.id]);

  useEffect(() => {
    const listener: PushStateListener = (patch) => {
      setState((prev) => ({ ...prev, ...patch }));
    };
    pushStateListeners.add(listener);

    if (!isNative) {
      setState((prev) => ({ ...prev, isSupported: false }));
      return () => {
        pushStateListeners.delete(listener);
      };
    }

    const initPush = async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        broadcastPushState({ isSupported: true });

        const permResult = await PushNotifications.checkPermissions();
        broadcastPushState({
          permissionStatus: permResult.receive as PushNotificationState["permissionStatus"],
        });

        if (!pushListenersAttached) {
          pushListenersAttached = true;

          // Listen for registration
          PushNotifications.addListener("registration", async (token) => {
            try {
              const saved = await persistPushToken(token.value, activeUserId);
              console.log(saved ? "Push token registered and saved" : "Push token pending sign-in");
            } catch (err) {
              console.error("Failed to save push token:", err);
              broadcastPushState({ isRegistered: false });
            }
          });

          PushNotifications.addListener("registrationError", (err) => {
            console.error("Push registration error:", err);
            pushRegistrationStarted = false;
            broadcastPushState({ isRegistered: false });
          });

          // Handle received notifications
          PushNotifications.addListener("pushNotificationReceived", (notification) => {
            console.log("Push notification received:", notification);
          });

          PushNotifications.addListener("pushNotificationActionPerformed", (notification) => {
            console.log("Push notification action:", notification);
            // Navigate to relevant airport if data includes airport code
            const airportCode = notification.notification.data?.airportCode;
            if (airportCode) {
              window.location.href = `/airports/${airportCode.toLowerCase()}`;
            }
          });
        }

        // Auto-register if already granted
        if (permResult.receive === "granted") {
          await registerForPush(PushNotifications);
        }
      } catch (err) {
        console.log("Push notifications not available:", err);
      }
    };

    if (!pushInitPromise) {
      pushInitPromise = initPush();
    }

    return () => {
      pushStateListeners.delete(listener);
    };
  }, [isNative]);

  const requestPermission = useCallback(async () => {
    if (!isNative) return false;

    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const result = await PushNotifications.requestPermissions();

      setState((prev) => ({
        ...prev,
        permissionStatus: result.receive as PushNotificationState["permissionStatus"],
      }));

      if (result.receive === "granted") {
        await registerForPush(PushNotifications);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [isNative]);

  const refreshRegistration = useCallback(async () => {
    if (!isNative) return false;

    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const result = await PushNotifications.checkPermissions();
      if (result.receive !== "granted") return false;
      await registerForPush(PushNotifications, true);
      return true;
    } catch (err) {
      console.error("Failed to refresh push registration:", err);
      return false;
    }
  }, [isNative]);

  return {
    ...state,
    requestPermission,
    refreshRegistration,
    isNative,
  };
};
